/**
 * Sniper v2 — sequence composer state (docs/260909SniperV2Design.md §5).
 *
 * The composer is the trader's PRE-ARMED intent: an op + its parameters, held
 * on screen with a plan readout and ghost legs painted on the ladder, fired
 * only by an explicit human action. Design principle 6 — auto-assist proposes,
 * the human fires — is structural here: nothing in this module fires on a
 * timer, on a cursor change, or on a store subscription. `fire()` has exactly
 * one caller shape (a keypress or a click), and the tests pin that.
 *
 * The client sends an OP and PARAMETERS, never legs. The relay
 * (`buildSequencePlan` behind POST /api/kalshi/order-sequence) stays the
 * authority: it expands the plan, pre-flights each place leg, and executes
 * with the existing abort semantics. The ghost legs and the readout built here
 * are a FAITHFUL LOCAL PREVIEW of that expansion for the trader's eyes — they
 * are never transmitted, and a divergence between preview and receipt is a
 * relay-truth win, not a client override.
 *
 * Wire body (final relay contract, 2026-09-09):
 *   { op, ticker, side, cursorPriceCents, levels?, count, blockerCount?,
 *     sibling?, sequenceId? }
 *   • `count` is PER TAKE LEG; the summed size is count × levels, doubled for
 *     both-sides.
 *   • `cursorPriceCents` is the first/best take price; deeper levels walk up.
 *   • `blockerCount` is REQUIRED for a blocker leg to exist — the relay plans
 *     none without it and answers `blockerPriceCents: null`.
 *   • `sibling` carries book B of a both-sides fire; each field defaults to
 *     book A's, and the optional `eventTicker` is omitted rather than guessed.
 *   • `take: {counts: [...]}` (per-level sizing) is accepted by the relay and
 *     deliberately unused here — one size per level is the armed-intent model.
 *
 * Response discipline, from §5 and the existing Oscar route:
 *   • 200 once execution begins, with per-leg results keyed by `roleKey`
 *     (`take`, `take2`, `take3`, `blocker`, `-a`/`-b` under both-sides);
 *   • a leg that was `already_gone` CONTINUES the sequence (not a failure);
 *   • `ambiguous` codes and 5xx/transport losses are UNVERIFIED, never
 *     "rejected" — a rejected order is one the venue refused, and calling a
 *     lost response a rejection is how a trader places a duplicate.
 */
import { create } from 'zustand';
import { apiFetch } from './authStore';
import { MIN_ORDER_SPACING_MS } from '../views/ValueDashboard/orderLadder';
import { lt2ClockStamp, recordLt2Timing, updateLt2Timing, type Lt2ClockStamp } from '../lib/trading/lt2Timing';

// ── Ops ──────────────────────────────────────────────────────────────────

export type SequenceOp =
  | 'passive'
  | 'attack'
  | 'jump'
  | 'overtake'
  | 'release_overtake'
  | 'overtake_through'
  | 'overtake_both'
  | 'pull_all';

export const SEQUENCE_OPS: readonly SequenceOp[] = [
  'passive',
  'attack',
  'jump',
  'overtake',
  'release_overtake',
  'overtake_through',
  'overtake_both',
  'pull_all',
];

/** Default key hint shown next to each op (§8; every binding is remappable in
 *  the registry — this is a LABEL, the listener owns the actual bindings). */
export const OP_KEY_HINT: Record<SequenceOp, string> = {
  passive: 'A / D',
  attack: 'W / E',
  jump: 'Q / R',
  overtake: 'F',
  release_overtake: 'Shift+F',
  overtake_through: 'G',
  overtake_both: 'Shift+G',
  pull_all: 'Space',
};

export const OP_LABEL: Record<SequenceOp, string> = {
  passive: 'Passive',
  attack: 'Attack',
  jump: 'Jump',
  overtake: 'Overtake',
  release_overtake: 'Release + overtake',
  overtake_through: 'Overtake through',
  overtake_both: 'Overtake both sides',
  pull_all: 'Pull all',
};

/** Ops whose plan is a multi-level take walk. */
const MULTI_LEVEL_OPS: ReadonlySet<SequenceOp> = new Set(['overtake_through', 'overtake_both']);
/** Ops that append a post-only blocker at 99 − worst take. */
const BLOCKER_OPS: ReadonlySet<SequenceOp> = new Set([
  'overtake',
  'release_overtake',
  'overtake_through',
  'overtake_both',
]);

/** Josh, 2026-09-09: `oscarMaxLevels` default 3. */
export const DEFAULT_OSCAR_MAX_LEVELS = 3;
/** Josh, 2026-09-09: one-click ceiling 10 when the trader carries no explicit
 *  server ceiling (`traderRules.oneClickMaxContracts`). Fail small, not big. */
export const DEFAULT_ONE_CLICK_CEILING = 10;

// ── Cursor / ghost legs ──────────────────────────────────────────────────

/**
 * The paint vocabulary the ladder understands — NOT the relay's leg roles.
 * `take` tints the levels the sequence will lift, `blocker` tints the
 * post-only leg in the opposite book, `kill` outlines a resting order the
 * sequence will cancel (own blocker on a release, friendly blockers on a
 * multi-level take, everything on a pull-all).
 */
export type GhostLegRole = 'take' | 'blocker' | 'kill';

export interface GhostLeg {
  /** Book id as the execution band knows it (e.g. 'away' / 'home' / ticker). */
  book: string;
  /** Ladder level index, or null for a leg addressed by price alone. */
  level: number | null;
  priceCents: number;
  role: GhostLegRole;
  /**
   * Contracts this leg will carry — the SAME number the fire body sends
   * (`count` per take leg, `blockerCount` for the blocker). A ghost that
   * advertised a size the request did not ask for would promise a leg the
   * relay never plans; kill legs carry null (the relay cancels what rests).
   */
  count: number | null;
}

export interface ComposerLevel {
  level: number;
  priceCents: number;
  /** Contracts resting at the level, when the band knows it (display only). */
  availableCount?: number | null;
}

export interface ComposerBook {
  /** Stable book id the ladder paints ghosts against. */
  book: string;
  ticker: string;
  /** The take side on this book. */
  side: 'yes' | 'no';
  /** Levels the take walks: index 0 IS the cursor's level, deeper after it.
   *  A short ladder clamps the plan — the composer never invents a price. */
  takeLadder: ComposerLevel[];
  /** The trader's own blocker resting here (the release leg's cancel). */
  ownBlocker?: { level: number | null; priceCents: number } | null;
  /** Friendly blockers a kill leg would target (outlined as ghosts). */
  killTargets?: { level: number | null; priceCents: number }[];
}

export interface ComposerCursor {
  primary: ComposerBook;
  /** The other book of the same event — the blocker's home, and the mirror
   *  half of `overtake_both`. */
  opposite?: ComposerBook | null;
  /** OP devig fair for the primary take side, in cents (display only). */
  fairCents?: number | null;
}

// ── Plan ─────────────────────────────────────────────────────────────────

export interface ComposerPlan {
  op: SequenceOp;
  /** Take legs actually planned (after clamping). */
  levels: number;
  requestedLevels: number;
  countPerLeg: number;
  /** Summed size the relay's `checkOrderLimit` will see (take legs only). */
  totalContracts: number;
  takePrices: number[];
  blockerPriceCents: number | null;
  /**
   * Contracts the blocker leg will carry, or null when this op plans no
   * blocker. The relay plans a blocker leg ONLY when the request carries a
   * blocker count, so this is what `blockerCount` on the wire must say — and
   * what the ghost leg and the readout's `BLK` already promise.
   */
  blockerCount: number | null;
  ghostLegs: GhostLeg[];
  /** The mirror half of `overtake_both`, as the relay's `sibling` block. */
  sibling: ComposerSiblingPlan | null;
  /** Signed dollars vs fair; negative = paying up. Null without a fair. */
  estDollars: number | null;
  readout: string;
  bothSides: boolean;
  /** Why the requested level count was reduced, when it was. */
  clampedBy: 'oscarMaxLevels' | 'ladder' | null;
}

/** Book B of a both-sides fire, in the relay's `sibling` shape. */
export interface ComposerSiblingPlan {
  ticker: string;
  side: 'yes' | 'no';
  cursorPriceCents: number;
  levels: number;
  count: number;
  blockerCount: number | null;
  takePrices: number[];
  blockerPriceCents: number | null;
}

function fmtEstDollars(v: number): string {
  const sign = v < 0 ? '−' : '+';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function takeWalk(book: ComposerBook, want: number): ComposerLevel[] {
  return book.takeLadder.slice(0, Math.max(0, want));
}

function killGhosts(book: ComposerBook | null | undefined, includeOwn: boolean): GhostLeg[] {
  if (!book) return [];
  const out: GhostLeg[] = [];
  for (const t of book.killTargets ?? []) {
    out.push({ book: book.book, level: t.level, priceCents: t.priceCents, role: 'kill', count: null });
  }
  if (includeOwn && book.ownBlocker) {
    out.push({
      book: book.book,
      level: book.ownBlocker.level,
      priceCents: book.ownBlocker.priceCents,
      role: 'kill',
      count: null,
    });
  }
  return out;
}

/**
 * Blocker price for a take walk: **99 − the worst (highest) take price**, so
 * the pair still sums to 99 and cannot self-cross under `taker_at_cross`
 * (§5). Whole cents only.
 */
export function blockerPriceFor(takePrices: number[]): number | null {
  if (takePrices.length === 0) return null;
  return 99 - Math.max(...takePrices);
}

function blockerGhost(
  cursor: ComposerCursor,
  home: ComposerBook,
  priceCents: number,
  count: number,
): GhostLeg {
  // The blocker rests in the OPPOSITE book of the same event (§4.1). When the
  // band gave us that book and its ladder carries the price, the ghost is
  // level-addressed; otherwise it is price-addressed and the ladder places it.
  const opposite = home === cursor.primary ? cursor.opposite ?? null : cursor.primary;
  const target = opposite ?? home;
  const level = target.takeLadder.find((l) => l.priceCents === priceCents)?.level ?? null;
  return { book: target.book, level, priceCents, role: 'blocker', count };
}

function planOneBook(
  cursor: ComposerCursor,
  book: ComposerBook,
  op: SequenceOp,
  wantLevels: number,
  countPerLeg: number,
): {
  takes: ComposerLevel[];
  ghosts: GhostLeg[];
  blockerPriceCents: number | null;
  blockerCount: number | null;
} {
  const takes = takeWalk(book, wantLevels);
  const ghosts: GhostLeg[] = [];
  ghosts.push(...killGhosts(book, op === 'release_overtake'));
  for (const lv of takes) {
    ghosts.push({
      book: book.book,
      level: lv.level,
      priceCents: lv.priceCents,
      role: 'take',
      count: countPerLeg,
    });
  }
  let blockerPriceCents: number | null = null;
  let blockerCount: number | null = null;
  if (BLOCKER_OPS.has(op)) {
    blockerPriceCents = blockerPriceFor(takes.map((t) => t.priceCents));
    if (blockerPriceCents != null) {
      // The relay plans a blocker ONLY when the request carries a blocker
      // count, so the size the ghost advertises IS the size the body asks
      // for: the whole take walk on this book, covered one-for-one.
      blockerCount = countPerLeg * takes.length;
      ghosts.push(blockerGhost(cursor, book, blockerPriceCents, blockerCount));
    }
  }
  return { takes, ghosts, blockerPriceCents, blockerCount };
}

export interface BuildPlanInput {
  op: SequenceOp;
  cursor: ComposerCursor | null;
  /** Requested take depth; only `_through` / `_both` read more than 1. */
  levels: number;
  /** Size PER take leg. The readout's `ct` is the summed take size. */
  countPerLeg: number;
  oscarMaxLevels?: number;
}

/**
 * Build the local preview of the relay's plan. Pure — no store, no fetch.
 */
export function buildComposerPlan(input: BuildPlanInput): ComposerPlan | null {
  const { op, cursor } = input;
  const oscarMaxLevels = Math.max(1, input.oscarMaxLevels ?? DEFAULT_OSCAR_MAX_LEVELS);
  const countPerLeg = Math.max(0, Math.floor(input.countPerLeg));
  if (!cursor) return null;

  const bothSides = op === 'overtake_both';
  const requestedLevels = MULTI_LEVEL_OPS.has(op) ? Math.max(1, Math.floor(input.levels)) : 1;

  if (op === 'pull_all') {
    const ghosts = [
      ...killGhosts(cursor.primary, true),
      ...killGhosts(cursor.opposite, true),
    ];
    return {
      op,
      levels: 0,
      requestedLevels: 0,
      countPerLeg: 0,
      totalContracts: 0,
      takePrices: [],
      blockerPriceCents: null,
      blockerCount: null,
      ghostLegs: ghosts,
      sibling: null,
      estDollars: null,
      readout: `${OP_KEY_HINT[op]}: pull all · ${ghosts.length} resting`,
      bothSides: false,
      clampedBy: null,
    };
  }

  const capped = Math.min(requestedLevels, oscarMaxLevels);
  const primary = planOneBook(cursor, cursor.primary, op, capped, countPerLeg);
  const levels = primary.takes.length;
  if (levels === 0) return null;

  const ghostLegs = [...primary.ghosts];
  let totalContracts = countPerLeg * levels;
  let mirrorLevels = 0;
  let sibling: ComposerSiblingPlan | null = null;
  if (bothSides && cursor.opposite) {
    const mirror = planOneBook(cursor, cursor.opposite, op, capped, countPerLeg);
    mirrorLevels = mirror.takes.length;
    ghostLegs.push(...mirror.ghosts);
    totalContracts += countPerLeg * mirrorLevels;
    if (mirrorLevels > 0) {
      sibling = {
        ticker: cursor.opposite.ticker,
        side: cursor.opposite.side,
        cursorPriceCents: mirror.takes[0].priceCents,
        levels: mirrorLevels,
        count: countPerLeg,
        blockerCount: mirror.blockerCount,
        takePrices: mirror.takes.map((t) => t.priceCents),
        blockerPriceCents: mirror.blockerPriceCents,
      };
    }
  }

  const clampedBy =
    requestedLevels > capped
      ? 'oscarMaxLevels'
      : levels < requestedLevels
        ? 'ladder'
        : null;

  const takePrices = primary.takes.map((t) => t.priceCents);
  const fair = cursor.fairCents;
  const estDollars =
    fair == null
      ? null
      : -(
          takePrices.reduce((sum, p) => sum + (p - fair), 0) * countPerLeg +
          (bothSides && cursor.opposite
            ? // The mirror leg's fair is the complement (100 − fair) on the
              // other outcome; without a second fair we price it off that.
              takeWalk(cursor.opposite, capped).reduce(
                (sum, l) => sum + (l.priceCents - (100 - fair)),
                0,
              ) * countPerLeg
            : 0)
        ) / 100;

  return {
    op,
    levels,
    requestedLevels,
    countPerLeg,
    totalContracts,
    takePrices,
    blockerPriceCents: primary.blockerPriceCents,
    blockerCount: primary.blockerCount,
    ghostLegs,
    sibling,
    estDollars,
    readout: buildReadout({
      op,
      takePrices,
      blockerPriceCents: primary.blockerPriceCents,
      totalContracts,
      estDollars,
      bothSides: bothSides && mirrorLevels > 0,
    }),
    bothSides,
    clampedBy,
  };
}

/**
 * The one-line plan readout, exactly the doc's shape (§5):
 *
 *   G: take 3 lv (31,32,33) + BLK 66 · 15 ct · est. −$0.42 vs fair
 */
export function buildReadout(input: {
  op: SequenceOp;
  takePrices: number[];
  blockerPriceCents: number | null;
  totalContracts: number;
  estDollars: number | null;
  bothSides: boolean;
}): string {
  const { op, takePrices, blockerPriceCents, totalContracts, estDollars, bothSides } = input;
  const prices = takePrices.join(',');
  const n = takePrices.length;
  let body: string;
  switch (op) {
    case 'passive':
      body = `post ${n} lv (${prices})`;
      break;
    case 'jump':
      body = `jump ${n} lv (${prices})`;
      break;
    case 'release_overtake':
      body = `release + take ${n} lv (${prices})`;
      break;
    default:
      body = `take ${n} lv (${prices})`;
      break;
  }
  if (blockerPriceCents != null) body += ` + BLK ${blockerPriceCents}`;
  if (bothSides) body += ' ×2 books';
  let out = `${OP_KEY_HINT[op]}: ${body} · ${totalContracts} ct`;
  if (estDollars != null) out += ` · est. ${fmtEstDollars(estDollars)} vs fair`;
  return out;
}

// ── Gates ────────────────────────────────────────────────────────────────

export interface ComposerGates {
  oneClickEnabled: boolean;
  lt2Enabled: boolean;
  /** The `attack` grant. */
  attackGrant: boolean;
  oscarEnabled: boolean;
  /** Relay flag OSCAR_MULTI_LEVEL. */
  multiLevelEnabled: boolean;
  /** Relay flag OSCAR_BOTH_SIDES. */
  bothSidesEnabled: boolean;
  /** traderRules.oneClickMaxContracts; null = no explicit ceiling. */
  maxContracts: number | null;
  oscarMaxLevels?: number;
}

export const DEFAULT_COMPOSER_GATES: ComposerGates = {
  oneClickEnabled: false,
  lt2Enabled: false,
  attackGrant: false,
  oscarEnabled: false,
  multiLevelEnabled: false,
  bothSidesEnabled: false,
  maxContracts: null,
  oscarMaxLevels: DEFAULT_OSCAR_MAX_LEVELS,
};

export type ComposerRefusalCode =
  | 'NO_CURSOR'
  | 'NO_PLAN'
  | 'ONE_CLICK_OFF'
  | 'LT2_OFF'
  | 'ATTACK_GRANT_MISSING'
  | 'OSCAR_OFF'
  | 'MULTI_LEVEL_FLAG_OFF'
  | 'BOTH_SIDES_FLAG_OFF'
  | 'NO_OPPOSITE_BOOK'
  | 'SIZE_ZERO'
  | 'SIZE_OVER_CEILING'
  | 'PACING';

export interface ComposerRefusal {
  code: ComposerRefusalCode;
  reason: string;
}

export type ComposerGateResult = { ok: true } | ({ ok: false } & ComposerRefusal);

export function composerCeiling(gates: ComposerGates): number {
  return gates.maxContracts ?? DEFAULT_ONE_CLICK_CEILING;
}

/**
 * Fail closed, fail loud (design principle 7). Every refusal names the gate
 * that is missing — the composer shows it and the FIRE control is dead until
 * it clears. This runs at RENDER (presentation) and again inside `fire()`
 * (enforcement); the second call is the one that matters.
 */
export function evaluateComposerGate(
  op: SequenceOp,
  plan: ComposerPlan | null,
  gates: ComposerGates,
  cursor?: ComposerCursor | null,
): ComposerGateResult {
  if (op !== 'pull_all') {
    if (!cursor && !plan) return { ok: false, code: 'NO_CURSOR', reason: 'No level cursor' };
    if (!plan) return { ok: false, code: 'NO_PLAN', reason: 'No plan at the cursor' };
  }
  const access = evaluateOpAccess(op, gates);
  if (!access.ok) return access;
  if (op === 'overtake_both') {
    if (plan && !plan.bothSides)
      return { ok: false, code: 'NO_OPPOSITE_BOOK', reason: 'No opposite book at the cursor' };
    if (cursor && !cursor.opposite)
      return { ok: false, code: 'NO_OPPOSITE_BOOK', reason: 'No opposite book at the cursor' };
  }
  if (op === 'pull_all') return { ok: true };
  if (plan) {
    if (plan.totalContracts <= 0) return { ok: false, code: 'SIZE_ZERO', reason: 'Size is 0' };
    const ceiling = composerCeiling(gates);
    if (plan.totalContracts > ceiling) {
      return {
        ok: false,
        code: 'SIZE_OVER_CEILING',
        reason: `${plan.totalContracts} ct over the ${ceiling} ct ceiling`,
      };
    }
  }
  return { ok: true };
}

/**
 * The op's ACCESS half only — grants and relay flags, with no cursor, plan or
 * size in the question. The op chips render from this so a flag-off op reads
 * as blocked before a cursor exists.
 */
export function evaluateOpAccess(op: SequenceOp, gates: ComposerGates): ComposerGateResult {
  switch (op) {
    case 'passive':
      if (!gates.oneClickEnabled)
        return { ok: false, code: 'ONE_CLICK_OFF', reason: 'One-click is not armed' };
      if (!gates.lt2Enabled) return { ok: false, code: 'LT2_OFF', reason: 'lt2Enabled is off' };
      break;
    case 'attack':
      if (!gates.attackGrant)
        return { ok: false, code: 'ATTACK_GRANT_MISSING', reason: 'attack grant missing' };
      break;
    case 'jump':
      if (!gates.lt2Enabled) return { ok: false, code: 'LT2_OFF', reason: 'lt2Enabled is off' };
      break;
    case 'overtake':
    case 'release_overtake':
      if (!gates.oscarEnabled)
        return { ok: false, code: 'OSCAR_OFF', reason: 'oscarEnabled is off' };
      break;
    case 'overtake_through':
      if (!gates.oscarEnabled)
        return { ok: false, code: 'OSCAR_OFF', reason: 'oscarEnabled is off' };
      if (!gates.multiLevelEnabled)
        return { ok: false, code: 'MULTI_LEVEL_FLAG_OFF', reason: 'OSCAR_MULTI_LEVEL is off' };
      break;
    case 'overtake_both':
      if (!gates.oscarEnabled)
        return { ok: false, code: 'OSCAR_OFF', reason: 'oscarEnabled is off' };
      if (!gates.bothSidesEnabled)
        return { ok: false, code: 'BOTH_SIDES_FLAG_OFF', reason: 'OSCAR_BOTH_SIDES is off' };
      break;
    case 'pull_all':
      break;
  }
  return { ok: true };
}

// ── Fire ─────────────────────────────────────────────────────────────────

export type ComposerLegStatus =
  | 'placed'
  | 'already_gone'
  | 'skipped'
  | 'unverified'
  | 'failed';

export interface ComposerLegResult {
  /**
   * The relay's `roleKey` — `take`, `take2`, `take3`, `blocker`, suffixed
   * `-a` / `-b` under both-sides. This is the leg's IDENTITY: receipts key
   * off it, because `role` alone collides across a multi-level walk.
   */
  roleKey: string;
  /** Base role with the ordinal and the both-sides suffix stripped. */
  role: string;
  legIndex: number | null;
  status: ComposerLegStatus;
  orderId: string | null;
  priceCents: number | null;
  count: number | null;
  level: number | null;
  message: string | null;
}

export type ComposerFireStatus =
  | 'pending'
  | 'accepted'
  | 'partial'
  | 'rejected'
  | 'unverified'
  | 'refused';

export interface ComposerFireResult {
  sequenceId: string;
  op: SequenceOp;
  ticker: string;
  status: ComposerFireStatus;
  /** The relay's message, VERBATIM when it sent one. */
  message: string | null;
  /** The relay's error code when it refused (e.g. OSCAR_SEQUENCE_SIZE_EXCEEDED). */
  code: string | null;
  legs: ComposerLegResult[];
  /** Local ack moment — the receipts strip measures ack→first-push from it. */
  ackAtMs: number;
  requestMs: number;
  responseMs: number | null;
  refusal: ComposerRefusal | null;
}

export interface ComposerWireBody {
  op: SequenceOp;
  ticker: string;
  side: 'yes' | 'no';
  /** The first/best take price; deeper levels walk up from it. */
  cursorPriceCents: number;
  levels?: number;
  /** Size PER take leg (confirmed with the relay). */
  count: number;
  /**
   * Size of the post-only blocker leg. The relay plans a blocker leg ONLY
   * when this (or `blocker: {count}`) is present — with the minimal body it
   * returns takes and `blockerPriceCents: null`. Sent whenever the plan, the
   * ghost legs and the readout's `BLK` advertise a blocker, so the preview can
   * never promise a leg the request did not ask for.
   */
  blockerCount?: number;
  /** Book B of a both-sides fire; every field defaults to book A's value. */
  sibling?: {
    ticker: string;
    side: 'yes' | 'no';
    cursorPriceCents: number;
    levels?: number;
    count: number;
    blocker?: { count: number };
  };
  sequenceId?: string;
}

export type ComposerFetch = (path: string, init: RequestInit) => Promise<Response>;

export interface FireArgs {
  /** Overrides the gates last pushed by the component (`setGates`). */
  gates?: ComposerGates;
  sequenceId?: string;
  /** Timing tag for the latency HUD ring. */
  inputMethod?: string;
  inputAtMs?: number | null;
  /** Same instant as `inputAtMs` on main's same-session monotonic clock
   *  (lt2ClockStamp) — what the LT2 timing buffer measures durations with. */
  inputAtClock?: Lt2ClockStamp | null;
  /** Test seam. Production uses `apiFetch`. */
  fetchImpl?: ComposerFetch;
}

const SEQUENCE_PATH = '/api/kalshi/order-sequence';
const FIRE_LOG_MAX = 20;

function newSequenceId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `seq-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function legStatusOf(leg: Record<string, unknown>): ComposerLegStatus {
  if (leg.skipped === true) return 'skipped';
  if (leg.alreadyGone === true) return 'already_gone';
  const code = typeof leg.code === 'string' ? leg.code.toUpperCase() : '';
  if (code.includes('AMBIG')) return 'unverified';
  if (leg.ok === true) return 'placed';
  return 'failed';
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Strip a `roleKey` down to its base role: `take3-b` → `take`, `blocker-a` →
 * `blocker`. The ordinal and the both-sides suffix are identity, not kind.
 */
export function baseRoleOf(roleKey: string): string {
  return roleKey.replace(/-(a|b)$/, '').replace(/\d+$/, '');
}

/** Normalize the relay's per-leg results. `roleKey` (`take`, `take2`,
 *  `blocker`, with `-a`/`-b` under both-sides) is the identity; `__legRole`
 *  and the older `role` shape are accepted as fallbacks. */
export function parseSequenceLegs(payload: unknown): ComposerLegResult[] {
  const legs = (payload as { legs?: unknown })?.legs;
  if (!Array.isArray(legs)) return [];
  return legs.map((raw, i) => {
    const leg = (raw ?? {}) as Record<string, unknown>;
    const roleKey =
      typeof leg.roleKey === 'string' && leg.roleKey !== ''
        ? leg.roleKey
        : typeof leg.__legRole === 'string'
          ? leg.__legRole
          : typeof leg.role === 'string'
            ? leg.role
            : 'leg';
    return {
      roleKey,
      role: baseRoleOf(roleKey),
      legIndex: num(leg.__legIndex) ?? i,
      status: legStatusOf(leg),
      orderId: typeof leg.orderId === 'string' ? leg.orderId : null,
      priceCents: num(leg.priceCents),
      count: num(leg.count),
      level: num(leg.level),
      message:
        typeof leg.error === 'string'
          ? leg.error
          : typeof leg.message === 'string'
            ? leg.message
            : null,
    };
  });
}

export function rollUpFireStatus(legs: ComposerLegResult[], bodyOk: boolean): ComposerFireStatus {
  if (legs.some((l) => l.status === 'unverified')) return 'unverified';
  if (legs.some((l) => l.status === 'failed')) return 'partial';
  // `already_gone` CONTINUES the sequence — it is not a failure (§5).
  return bodyOk ? 'accepted' : 'partial';
}

// ── Store ────────────────────────────────────────────────────────────────

interface SequenceComposerState {
  op: SequenceOp;
  levels: number;
  countPerLeg: number;
  cursor: ComposerCursor | null;
  gates: ComposerGates;
  plan: ComposerPlan | null;
  firing: boolean;
  lastRefusal: (ComposerRefusal & { atMs: number }) | null;
  lastFire: ComposerFireResult | null;
  /** Newest first, capped. The receipts strip renders these. */
  fireLog: ComposerFireResult[];

  setOp: (op: SequenceOp) => void;
  setLevels: (n: number) => void;
  setCountPerLeg: (n: number) => void;
  setCursor: (cursor: ComposerCursor | null) => void;
  setGates: (gates: ComposerGates) => void;
  clearRefusal: () => void;
  /** Patch a logged fire in place (the receipts strip stamps push arrivals). */
  patchFire: (sequenceId: string, patch: Partial<ComposerFireResult>) => void;
  /** THE only execution entry point. Never called by an effect or a timer. */
  fire: (args?: FireArgs) => Promise<ComposerFireResult>;
}

let _lastFireAtMs = 0;

/** Test seam — reset the module-level pacing fence. */
export function _resetComposerPacingForTests(): void {
  _lastFireAtMs = 0;
}

function cursorSignature(c: ComposerCursor | null): string {
  if (!c) return '';
  const book = (b: ComposerBook | null | undefined) =>
    b
      ? `${b.book}|${b.ticker}|${b.side}|${b.takeLadder
          .map((l) => `${l.level}:${l.priceCents}`)
          .join('.')}|${b.ownBlocker?.priceCents ?? ''}|${(b.killTargets ?? [])
          .map((k) => k.priceCents)
          .join('.')}`
      : '';
  return `${book(c.primary)}~${book(c.opposite)}~${c.fairCents ?? ''}`;
}

function recompute(
  s: Pick<SequenceComposerState, 'op' | 'cursor' | 'levels' | 'countPerLeg' | 'gates'>,
): ComposerPlan | null {
  return buildComposerPlan({
    op: s.op,
    cursor: s.cursor,
    levels: s.levels,
    countPerLeg: s.countPerLeg,
    oscarMaxLevels: s.gates.oscarMaxLevels ?? DEFAULT_OSCAR_MAX_LEVELS,
  });
}

export const useSequenceComposerStore = create<SequenceComposerState>((set, get) => ({
  op: 'overtake',
  levels: 1,
  countPerLeg: 5,
  cursor: null,
  gates: DEFAULT_COMPOSER_GATES,
  plan: null,
  firing: false,
  lastRefusal: null,
  lastFire: null,
  fireLog: [],

  setOp: (op) => {
    if (get().op === op) return;
    const next = { ...get(), op };
    set({ op, plan: recompute(next), lastRefusal: null });
  },
  setLevels: (n) => {
    const levels = Math.max(1, Math.floor(n));
    if (get().levels === levels) return;
    const next = { ...get(), levels };
    set({ levels, plan: recompute(next), lastRefusal: null });
  },
  setCountPerLeg: (n) => {
    const countPerLeg = Math.max(0, Math.floor(n));
    if (get().countPerLeg === countPerLeg) return;
    const next = { ...get(), countPerLeg };
    set({ countPerLeg, plan: recompute(next), lastRefusal: null });
  },
  setCursor: (cursor) => {
    // Signature-compared: the band may hand a fresh object every render, and
    // a churning cursor would republish ghost legs on every frame.
    if (cursorSignature(get().cursor) === cursorSignature(cursor)) return;
    const next = { ...get(), cursor };
    set({ cursor, plan: recompute(next) });
  },
  setGates: (gates) => {
    const prev = get().gates;
    if (
      prev.oneClickEnabled === gates.oneClickEnabled &&
      prev.lt2Enabled === gates.lt2Enabled &&
      prev.attackGrant === gates.attackGrant &&
      prev.oscarEnabled === gates.oscarEnabled &&
      prev.multiLevelEnabled === gates.multiLevelEnabled &&
      prev.bothSidesEnabled === gates.bothSidesEnabled &&
      prev.maxContracts === gates.maxContracts &&
      prev.oscarMaxLevels === gates.oscarMaxLevels
    ) {
      return;
    }
    const next = { ...get(), gates };
    set({ gates, plan: recompute(next) });
  },
  clearRefusal: () => set({ lastRefusal: null }),

  patchFire: (sequenceId, patch) => {
    const { fireLog, lastFire } = get();
    let touched = false;
    const nextLog = fireLog.map((f) => {
      if (f.sequenceId !== sequenceId) return f;
      touched = true;
      return { ...f, ...patch };
    });
    if (!touched) return;
    set({
      fireLog: nextLog,
      lastFire:
        lastFire && lastFire.sequenceId === sequenceId ? { ...lastFire, ...patch } : lastFire,
    });
  },

  fire: async (args: FireArgs = {}) => {
    const state = get();
    const gates = args.gates ?? state.gates;
    const plan = state.plan;
    const op = state.op;
    const ticker = state.cursor?.primary.ticker ?? '';
    const inputMethod = args.inputMethod ?? 'composer';
    const guardAt = lt2ClockStamp();
    const nowMs = guardAt.wallMs;

    const refuse = (refusal: ComposerRefusal): ComposerFireResult => {
      const result: ComposerFireResult = {
        sequenceId: args.sequenceId ?? '',
        op,
        ticker,
        status: 'refused',
        message: refusal.reason,
        code: refusal.code,
        legs: [],
        ackAtMs: Date.now(),
        requestMs: 0,
        responseMs: null,
        refusal,
      };
      set({ lastRefusal: { ...refusal, atMs: Date.now() }, lastFire: result });
      recordLt2Timing({
        inputMethod,
        outcome: refusal.code === 'PACING' ? 'throttled' : 'guard-refused',
        inputAt: args.inputAtClock ?? null,
        guard: guardAt,
        ack: lt2ClockStamp(),
        request: null,
        final: lt2ClockStamp(),
        finalOutcome: 'terminal-at-ack',
      });
      return result;
    };

    // ENFORCEMENT gate — the render-time chip is presentation.
    const gate = evaluateComposerGate(op, plan, gates, state.cursor);
    if (!gate.ok) return refuse({ code: gate.code, reason: gate.reason });
    if (!plan || !state.cursor) {
      return refuse({ code: 'NO_PLAN', reason: 'No plan at the cursor' });
    }

    // Pacing — drop, never queue (the same window one-click uses).
    if (nowMs - _lastFireAtMs < MIN_ORDER_SPACING_MS) {
      return refuse({ code: 'PACING', reason: 'Dropped — pacing (≤4/sec)' });
    }
    _lastFireAtMs = nowMs;

    const sequenceId = args.sequenceId ?? newSequenceId();
    const body: ComposerWireBody = {
      op,
      ticker: state.cursor.primary.ticker,
      side: state.cursor.primary.side,
      cursorPriceCents: state.cursor.primary.takeLadder[0]?.priceCents ?? plan.takePrices[0],
      count: plan.countPerLeg,
      sequenceId,
    };
    if (MULTI_LEVEL_OPS.has(op)) body.levels = plan.levels;
    // The relay plans a blocker ONLY when the request asks for one. The plan,
    // the ghost leg and the readout's `BLK` all advertise the same number, so
    // it has to go on the wire or the preview is a promise nothing keeps.
    if (plan.blockerCount != null) body.blockerCount = plan.blockerCount;
    if (plan.sibling) {
      // Book B of a both-sides fire. `eventTicker` is deliberately OMITTED:
      // it is optional and the relay rejects a value that is not exactly the
      // shared prefix, so guessing it can only turn a good fire into a 400.
      body.sibling = {
        ticker: plan.sibling.ticker,
        side: plan.sibling.side,
        cursorPriceCents: plan.sibling.cursorPriceCents,
        count: plan.sibling.count,
        ...(MULTI_LEVEL_OPS.has(op) ? { levels: plan.sibling.levels } : {}),
        ...(plan.sibling.blockerCount != null
          ? { blocker: { count: plan.sibling.blockerCount } }
          : {}),
      };
    }

    const doFetch = args.fetchImpl ?? apiFetch;
    // FETCH FIRST — the POST is dispatched before any setState, before the
    // timing record, before anything re-renders. Same discipline as
    // `oneClickStore.submitIntent`; nothing between the fence and this line
    // does work. The promise is awaited below.
    const requestAt = lt2ClockStamp();
    const requestMs = requestAt.wallMs;
    const init: RequestInit & { priority?: 'high' } = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      priority: 'high',
    };
    const inFlight = doFetch(SEQUENCE_PATH, init);

    const ackAt = lt2ClockStamp();
    const ackAtMs = ackAt.wallMs;
    const pending: ComposerFireResult = {
      sequenceId,
      op,
      ticker: body.ticker,
      status: 'pending',
      message: null,
      code: null,
      legs: [],
      ackAtMs,
      requestMs,
      responseMs: null,
      refusal: null,
    };
    set({
      firing: true,
      lastRefusal: null,
      lastFire: pending,
      fireLog: [pending, ...get().fireLog].slice(0, FIRE_LOG_MAX),
    });
    const timingId = recordLt2Timing({
      inputMethod,
      outcome: 'pending',
      inputAt: args.inputAtClock ?? null,
      guard: guardAt,
      ack: ackAt,
      request: requestAt,
    });

    const settle = (patch: Partial<ComposerFireResult>): ComposerFireResult => {
      const result: ComposerFireResult = { ...pending, ...patch };
      set({
        firing: false,
        lastFire: result,
        fireLog: get().fireLog.map((f) => (f.sequenceId === sequenceId ? result : f)),
      });
      return result;
    };

    let res: Response;
    try {
      res = await inFlight;
    } catch (err) {
      // A lost response is NOT a rejection: the sequence may be live at the
      // venue. Unverified, loudly.
      const responseAt = lt2ClockStamp();
      const responseMs = responseAt.wallMs;
      updateLt2Timing(timingId, {
        outcome: 'unverified',
        response: responseAt,
        final: responseAt,
        finalOutcome: 'unverified',
      });
      return settle({
        status: 'unverified',
        responseMs,
        message: `No response — unverified (${
          err instanceof Error && err.message ? err.message : 'network'
        })`,
      });
    }

    const responseAt = lt2ClockStamp();
    const responseMs = responseAt.wallMs;
    const payload: unknown = await res.json().catch(() => null);

    if (!res.ok) {
      // 5xx / 408 → the relay may have begun executing: unverified.
      const ambiguous = res.status >= 500 || res.status === 408;
      const errBody = payload as { error?: string; code?: string } | null;
      // VERBATIM. A ceiling refusal (403 OSCAR_SEQUENCE_SIZE_EXCEEDED) names
      // the arithmetic it used — re-deriving it here would eventually print a
      // different sum than the one that actually refused the order.
      const message =
        typeof errBody?.error === 'string' && errBody.error !== ''
          ? errBody.error
          : ambiguous
            ? `Unverified (HTTP ${res.status})`
            : `Rejected (HTTP ${res.status})`;
      updateLt2Timing(timingId, {
        outcome: ambiguous ? 'unverified' : 'rejected',
        response: responseAt,
        final: responseAt,
        finalOutcome: ambiguous ? 'unverified' : 'rejected',
      });
      return settle({
        status: ambiguous ? 'unverified' : 'rejected',
        responseMs,
        message,
        code: typeof errBody?.code === 'string' ? errBody.code : null,
      });
    }

    const legs = parseSequenceLegs(payload);
    const bodyOk = (payload as { ok?: boolean } | null)?.ok === true;
    const status = rollUpFireStatus(legs, bodyOk);
    updateLt2Timing(timingId, {
      outcome: status === 'unverified' ? 'unverified' : status === 'accepted' ? 'accepted' : 'rejected',
      response: responseAt,
      final: responseAt,
      finalOutcome: status,
    });
    return settle({
      status,
      responseMs,
      legs,
      message: legs.find((l) => l.status === 'failed' || l.status === 'unverified')?.message ?? null,
    });
  },
}));

/**
 * Zero-argument entry point for the hotkey listener (which owns the registry
 * and must not know the composer's props). Uses the gates the component last
 * pushed with `setGates`; still an EXPLICIT call — nothing here self-fires.
 */
export function fireArmedSequence(args: FireArgs = {}): Promise<ComposerFireResult> {
  return useSequenceComposerStore.getState().fire(args);
}
